import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Play, Pause } from 'lucide-react';

const SUPABASE_PROJECT_ID = 'mosvpddcjtcbnineqzqh'; 
const BUCKET_URL = `https://${SUPABASE_PROJECT_ID}.supabase.co/storage/v1/object/public/exercise-videos`;

const ExerciseDetail = ({ exercise, onBack }) => {
  const [tab, setTab] = useState("instructions");
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const videoUrl = exercise.videoPath 
    ? `${BUCKET_URL}/${exercise.videoPath.split('/').map(s => encodeURIComponent(s)).join('/')}`
    : null;

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <motion.div
      initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
      className="fixed inset-0 bg-white z-[80] overflow-y-auto"
    >
      {/* Click-to-Play Video Header */}
      <div className="relative h-96 bg-black flex items-center justify-center group" onClick={togglePlay}>
        
        {videoUrl && (
          <video 
            ref={videoRef}
            src={videoUrl} 
            preload="metadata" // Don't download the full 7s until play is clicked
            loop 
            playsInline 
            className="w-full h-full object-cover opacity-80" 
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        )}

        {/* Play/Pause Overlay */}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isPlaying ? 'opacity-0' : 'opacity-100'}`}>
          <div className="p-6 bg-blue-600 rounded-full shadow-2xl text-white transform transition-transform active:scale-90">
            {isPlaying ? <Pause size={32} fill="currentColor" /> : <Play size={32} fill="currentColor" />}
          </div>
        </div>

        <button 
          onClick={(e) => { e.stopPropagation(); onBack(); }} 
          className="absolute top-12 left-4 p-2 bg-white/20 backdrop-blur-md rounded-full text-white z-50 shadow-lg"
        >
          <ArrowLeft size={22} />
        </button>
      </div>

      <div className="p-6">
        <h1 className="text-3xl font-black text-gray-900 mb-6">{exercise.name}</h1>
        
        <div className="flex bg-gray-100 rounded-2xl p-1 mb-8">
          {["instructions", "record"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${tab === t ? "bg-white shadow text-black" : "text-gray-400"}`}>
              {t === "instructions" ? "Instructions" : "Personal Record"}
            </button>
          ))}
        </div>

        {tab === "instructions" ? (
          <div className="space-y-8 pb-32">
            <section>
              <h3 className="font-black text-gray-900 mb-3 uppercase tracking-tight">Target Muscles</h3>
              <div className="flex flex-wrap gap-2">
                {exercise.muscles?.map(m => (
                  <span key={m} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-bold border border-blue-100">● {m}</span>
                ))}
              </div>
            </section>

            <section>
              <h3 className="font-black text-gray-900 mb-4 uppercase tracking-tight">Execution</h3>
              {exercise.steps?.map((step, i) => (
                <div key={i} className="flex gap-4 mb-4">
                  <div className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</div>
                  <p className="text-sm text-gray-600 leading-relaxed font-medium">{step}</p>
                </div>
              ))}
            </section>
          </div>
        ) : (
          <div className="py-20 text-center text-gray-400 flex flex-col items-center">
            <CheckCircle2 className="mb-4 text-gray-200" size={64} />
            <p className="font-bold">Progress tracking starts soon!</p>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white via-white to-transparent pt-10">
        <button onClick={onBack} className="w-full bg-blue-600 text-white py-5 rounded-[24px] font-black text-lg shadow-2xl active:scale-[0.98] transition-transform">
          Complete Exercise
        </button>
      </div>
    </motion.div>
  );
};

export default ExerciseDetail;